import {
  HHAlert,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useMemo } from 'react';
import { ActiveContractLabels } from '../constants/strings/billing-label-enums';
import { newContractLabel } from '../constants/strings/header-labels';
import { routes } from '../constants/strings/routes';
import { useGetAllDiscountsPaginated } from '../hooks/discount-hooks';
import {
  ClientInsurerType,
  useGetContractsV2Query,
  useGetContractTemplatesQuery,
} from '../types';
import {
  checkContractsForGaps,
  findActiveContract,
  sortContracts,
} from '../utils/contract-utils';
import { cleanTypeNameGraphql } from '../utils/graphql-utils';
import ClientDiscountsList from './clients/client-discounts';
import { EditContractButton } from './contract/billing-edit-contract-button';
import { NewContractButton } from './contract/billing-new-contract-button';
import ActiveContractDetails from './contract/billing-tool-active-contract-details';
import ContractGrid from './contract/billing-tool-contract-grid';
import { ContractLoadingError } from './contract/contract-view-components/contract-loading-error';
import { ResolvedProcedureCodesGrid } from './resolved-procedure-codes/resolved-procedure-codes-grid';
import LoadingComponent from './utils/loading';

export const MAIN_SECTION_TEST_ID = 'billing-tool-main-section';

export interface BillingMainSectionProps {
  clientInsurerData: ClientInsurerType;
}

const BillingMainSection = ({
  clientInsurerData,
}: BillingMainSectionProps): JSX.Element => {
  const {
    data: contractData,
    loading: contractLoading,
    error: contractError,
  } = useGetContractsV2Query({
    // Refetch on navigation so a newly created or edited contract appears
    // immediately rather than only after a hard refresh.
    fetchPolicy: 'cache-and-network',
    variables: {
      clientsInsurerIds: [clientInsurerData.id],
      includeVoided: true,
      page: 1,
      size: 10,
    },
  });
  const { data, loading, error } = useGetContractTemplatesQuery();

  const contracts = useMemo(
    () => sortContracts(contractData?.getContractsV2?.items ?? []),
    [contractData],
  );

  const partnershipIds = useMemo(
    () =>
      contracts
        .filter(c => !c.void)
        .map(c => c.partnershipId)
        .filter((id): id is number => id !== null && id !== undefined),
    [contracts],
  );

  const { data: discountsData } = useGetAllDiscountsPaginated({
    clientId: clientInsurerData.client.id,
    partnershipIds,
  });

  if (
    (!loading && (error || !data)) ||
    (!contractLoading && (contractError || !contractData))
  ) {
    return <ContractLoadingError />;
  }

  const activeContract = findActiveContract(contracts);
  const contractTemplates = data?.getContractTemplates ?? [];

  const gapExists = checkContractsForGaps(cleanTypeNameGraphql(contracts));

  return (
    <HHStack
      direction="column"
      data-testid={MAIN_SECTION_TEST_ID}
      flex={1}
      padding={4}
      sx={{
        overflowY: 'auto',
      }}
    >
      {contractLoading || loading ? (
        <LoadingComponent center />
      ) : (
        <HHStack direction="column" spacing={6}>
          {gapExists && (
            <HHAlert severity="warning" hhVariant="outlined">
              <HHTypography hhVariant="body">
                There is a gap between contracts
              </HHTypography>
            </HHAlert>
          )}
          <HHStack direction="column" spacing={2}>
            <HHStack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={6}
            >
              <HHTypography hhVariant="h2">
                {ActiveContractLabels.ACTIVE_CONTRACT_LABEL}
              </HHTypography>
              <HHStack direction="row" spacing={2}>
                {activeContract && activeContract.packageEnabled && (
                  <EditContractButton
                    isText={false}
                    contractId={activeContract?.id}
                    buttonVariant="text"
                    route={routes.billing.editContractPackageContract}
                  />
                )}
                <NewContractButton
                  label={newContractLabel}
                  route={routes.billing.newContractPackage}
                  variant="contained"
                />
              </HHStack>
            </HHStack>
            <ActiveContractDetails
              contract={activeContract}
              clientInsurerName={clientInsurerData.client?.name}
              templates={contractTemplates}
              discounts={discountsData}
            />
          </HHStack>
          <HHStack direction="column" spacing={12}>
            <ContractGrid
              clientInsurerId={clientInsurerData.id}
              templates={contractTemplates}
              discounts={discountsData}
            />
            <ClientDiscountsList
              clientId={clientInsurerData.client.id}
              partnershipIds={partnershipIds}
              disableAddNew
            />
            {activeContract &&
              activeContract.billingType?.toLowerCase().includes('waystar') && (
                <ResolvedProcedureCodesGrid
                  clientId={activeContract.clientId}
                  insurerId={activeContract.insurerId}
                  partnershipId={activeContract.partnershipId ?? undefined}
                />
              )}
          </HHStack>
        </HHStack>
      )}
    </HHStack>
  );
};

export default BillingMainSection;
